import React, { useState, useEffect, useRef } from "react";
import { Form, Button, Message, Icon } from "semantic-ui-react";
import { useCustomer, useInvoicing } from "../../../hooks";
import { isValidRuc, isValidDni } from "../../../utils/documents";

const KIND_OPTIONS = [
  { key: "PERSON", value: "PERSON", text: "Persona" },
  { key: "COMPANY", value: "COMPANY", text: "Empresa" },
];
// "NONE" y no "": el Dropdown de Semantic trata el string vacío como "sin
// selección" y se veía en blanco. Se convierte a "" al guardar.
const NO_DOC = "NONE";
const DOC_OPTIONS = [
  { key: "none", value: NO_DOC, text: "Sin documento" },
  { key: "DNI", value: "DNI", text: "DNI" },
  { key: "RUC", value: "RUC", text: "RUC" },
  { key: "CE", value: "CE", text: "Carné de extranjería" },
];

const EMPTY = {
  kind: "PERSON",
  first_name: "",
  last_name: "",
  phone: "",
  doc_type: "",
  doc_number: "",
  email: "",
  address: "",
  birth_date: "",
};

// Nuevo / editar cliente, en dos pestañas como Restaurant.pe: Básico (el
// documento y lo mínimo para tomar el pedido o emitir un comprobante) y
// Avanzado (tipo, email, nacimiento). Al ingresar un DNI o RUC válido se
// consultan solos los datos (SUNAT/RENIEC), como en SIG.
export function CustomerForm(props) {
  const { customer, onSaved, onCancel } = props;
  const { createCustomer, updateCustomer } = useCustomer();
  const { lookupDocument } = useInvoicing();
  const [lookup, setLookup] = useState(null);
  const [looking, setLooking] = useState(false);
  const [tab, setTab] = useState("basic");
  const [values, setValues] = useState(() => ({
    ...EMPTY,
    ...(customer || {}),
    birth_date: customer?.birth_date || "",
    doc_type: customer?.doc_type || NO_DOC,
  }));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const set = (field) => (_, data) =>
    setValues((prev) => ({ ...prev, [field]: data.value }));
  const isCompany = values.kind === "COMPANY";

  const save = async () => {
    if (!values.first_name.trim()) {
      setErrors({ first_name: isCompany ? "Ingresa la razón social" : "Ingresa los nombres" });
      setTab("basic");
      return;
    }
    setSaving(true);
    setErrors({});
    const noDoc = values.doc_type === NO_DOC;
    const payload = {
      ...values,
      birth_date: values.birth_date || null,
      doc_type: noDoc ? "" : values.doc_type,
      doc_number: noDoc ? "" : values.doc_number,
    };
    delete payload.id;
    delete payload.full_name;
    delete payload.created_at;
    try {
      const saved = customer?.id
        ? await updateCustomer(customer.id, payload)
        : await createCustomer(payload);
      onSaved(saved);
    } catch (error) {
      const fields = error.fields || {};
      setErrors(
        Object.fromEntries(
          Object.entries(fields).map(([key, value]) => [key, [].concat(value).join(" ")])
        )
      );
      if (fields.email) setTab("advanced");
      else if (fields.doc_number || fields.doc_type) setTab("basic");
      setSaving(false);
    }
  };

  // Aviso inmediato si el número no cuadra (RUC: dígito verificador), sin consultar.
  const docNumber = values.doc_type === NO_DOC ? "" : values.doc_number;
  const docInvalid =
    (values.doc_type === "RUC" && docNumber.length === 11 && !isValidRuc(docNumber)) ||
    (values.doc_type === "DNI" && docNumber.length === 8 && !isValidDni(docNumber));
  const canLookup =
    (values.doc_type === "RUC" && isValidRuc(docNumber)) ||
    (values.doc_type === "DNI" && isValidDni(docNumber));

  // Valida el número y, si el negocio configuró la consulta, trae el nombre y
  // la dirección de SUNAT/RENIEC (mejor esfuerzo: si no, se completa a mano).
  const onLookup = async () => {
    setLooking(true);
    setLookup(null);
    try {
      const result = await lookupDocument(values.doc_type, docNumber);
      if (!result.valid) {
        setLookup({ color: "red", text: "El número no es válido: revisa los dígitos." });
      } else if (!result.configured) {
        setLookup({
          color: "blue",
          text: "Número válido. La consulta automática a SUNAT no está configurada: completa los datos a mano.",
        });
      } else if (!result.found) {
        // Motivo que devuelve el servidor: el número no existe, la consulta fue
        // rechazada (token inválido o límite del plan agotado) o no se pudo.
        const reasons = {
          not_found: "No se encontró en SUNAT/RENIEC. Revisa el número o completa los datos a mano.",
          rejected: "La consulta fue rechazada (token inválido o límite mensual de consultas agotado). Completa los datos a mano y avisa a soporte para que revise la consulta.",
        };
        setLookup({
          color: "orange",
          text: reasons[result.error] || "No se pudo consultar ahora. Completa los datos a mano.",
        });
      } else {
        setValues((prev) => ({
          ...prev,
          first_name: result.name,
          last_name: "",
          address: result.address || prev.address,
          kind: values.doc_type === "RUC" && docNumber.startsWith("20") ? "COMPANY" : "PERSON",
        }));
        const warn = (result.status && result.status !== "ACTIVO") || (result.condition && result.condition !== "HABIDO");
        const detail = result.status ? " · " + result.status + (result.condition ? " · " + result.condition : "") : "";
        setLookup({ color: warn ? "orange" : "green", text: "Encontrado: " + result.name + detail });
      }
    } catch (error) {
      setLookup({ color: "orange", text: "No se pudo consultar ahora. Completa los datos a mano." });
    } finally {
      setLooking(false);
    }
  };

  // Consulta automática: apenas el número es válido (con una pausa corta por si
  // aún se está escribiendo), sin tocar el botón. Un cliente que se abre para
  // editar no se vuelve a consultar hasta que cambie su número.
  const lookedUpRef = useRef(customer?.doc_number ? `${customer.doc_type}:${customer.doc_number}` : "");
  useEffect(() => {
    if (!canLookup) return undefined;
    const key = `${values.doc_type}:${docNumber}`;
    if (lookedUpRef.current === key) return undefined;
    const timer = setTimeout(() => {
      lookedUpRef.current = key;
      onLookup();
    }, 400);
    return () => clearTimeout(timer);
  }, [values.doc_type, docNumber]);

  const fieldError = (name) => (errors[name] ? { content: errors[name] } : null);

  return (
    <Form className="customer-form">
      <Button.Group fluid className="customer-form__tabs">
        <Button type="button" active={tab === "basic"} primary={tab === "basic"} onClick={() => setTab("basic")}>
          Básico
        </Button>
        <Button type="button" active={tab === "advanced"} primary={tab === "advanced"} onClick={() => setTab("advanced")}>
          Avanzado
        </Button>
      </Button.Group>

      {tab === "basic" && (
        <>
          <Form.Group>
            <Form.Select
              width={5}
              label="Documento"
              options={DOC_OPTIONS}
              value={values.doc_type}
              onChange={set("doc_type")}
              error={fieldError("doc_type")}
            />
            <Form.Input
              width={6}
              label="Número"
              placeholder={values.doc_type === "RUC" ? "11 dígitos" : values.doc_type === "DNI" ? "8 dígitos" : ""}
              maxLength={values.doc_type === "RUC" ? 11 : values.doc_type === "DNI" ? 8 : 20}
              value={values.doc_type === NO_DOC ? "" : values.doc_number}
              onChange={(_, data) =>
                setValues((prev) => ({ ...prev, doc_number: data.value.replace(/[^0-9A-Za-z]/g, "") }))
              }
              disabled={values.doc_type === NO_DOC}
              error={
                fieldError("doc_number") ||
                (docInvalid
                  ? { content: values.doc_type === "RUC" ? "El RUC no es válido (revisa los dígitos)." : "El DNI debe tener 8 dígitos." }
                  : null)
              }
            />
            {(values.doc_type === "RUC" || values.doc_type === "DNI") && (
              <Form.Field width={5}>
                <label>&nbsp;</label>
                <Button type="button" basic fluid loading={looking} disabled={!canLookup || looking} onClick={onLookup}>
                  <Icon name="search" /> Consultar
                </Button>
              </Form.Field>
            )}
          </Form.Group>
          {lookup && (
            <Message size="small" color={lookup.color} style={{ marginTop: 0 }}>
              {lookup.text}
            </Message>
          )}

          <Form.Group widths="equal">
            <Form.Input
              label={isCompany ? "Razón social" : values.doc_type === NO_DOC ? "Nombres" : "Nombre / Razón social"}
              value={values.first_name}
              onChange={set("first_name")}
              error={fieldError("first_name")}
            />
            {!isCompany && (
              <Form.Input label="Apellidos (opcional)" value={values.last_name} onChange={set("last_name")} />
            )}
          </Form.Group>
          <Form.Group widths="equal">
            <Form.Input label="Dirección (opcional)" placeholder="Para delivery o comprobante" value={values.address} onChange={set("address")} />
            <Form.Input label="Teléfono (opcional)" value={values.phone} onChange={set("phone")} />
          </Form.Group>
        </>
      )}

      {tab === "advanced" && (
        <>
          <Form.Select label="Tipo de cliente" options={KIND_OPTIONS} value={values.kind} onChange={set("kind")} />
          <Form.Group widths="equal">
            <Form.Input label="Email" type="email" value={values.email} onChange={set("email")} error={fieldError("email")} />
            {!isCompany && (
              <Form.Input label="Fecha de nacimiento" type="date" value={values.birth_date} onChange={set("birth_date")} />
            )}
          </Form.Group>
        </>
      )}

      {errors.non_field_errors && <Message negative size="small">{errors.non_field_errors}</Message>}

      <div className="customer-form__actions">
        <Button type="button" basic onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="button" primary loading={saving} disabled={saving} onClick={save}>
          Guardar
        </Button>
      </div>
    </Form>
  );
}
